import type { ReactNode } from 'react';
import { cn } from '~/lib/cn.js';

export function Alert({
  tone = 'error',
  title,
  children,
}: {
  tone?: 'error' | 'info' | 'success';
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    error: 'border-red-600/40 bg-red-600/5 text-red-800 dark:text-red-200',
    info: 'border-line-strong bg-surface text-body',
    success: 'border-primary/40 bg-primary/5 text-body',
  } as const;

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('rounded-lg border px-4 py-3 text-sm', tones[tone])}
    >
      {title ? <p className="mb-1 font-medium">{title}</p> : null}
      {children}
    </div>
  );
}

/** Non-blocking loading state that announces itself rather than flashing silently. */
export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-3 py-10 text-sm text-muted">
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-line-strong border-t-primary motion-reduce:animate-none"
      />
      {label}…
    </div>
  );
}

export function EmptyState({ heading, children }: { heading: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-line px-6 py-12 text-center">
      <p className="display-3">{heading}</p>
      {children ? <div className="mx-auto mt-3 max-w-prose text-sm text-muted">{children}</div> : null}
    </div>
  );
}

export function ErrorState({ heading, children }: { heading: string; children?: ReactNode }) {
  return (
    <div role="alert" className="rounded-lg border border-line px-6 py-12 text-center">
      <p className="display-3">{heading}</p>
      {children ? <div className="mx-auto mt-3 max-w-prose text-sm text-muted">{children}</div> : null}
    </div>
  );
}
